import {
  YES_NO,
  applicantSection,
  feeProofAttachment,
  otherAttachments,
  proxyAttachments,
  representationSection,
} from "modules/ebiForms/common";
import { electronicDeliverySection, parcelIdsField } from "modules/ebiForms/definitions/sharedPlanningTaxes";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiField, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * A1. Wniosek o ustalenie warunków zabudowy (albo lokalizacji inwestycji celu publicznego) — art. 52 ust. 1, art. 59,
 * art. 61, art. 64 ust. 1 i art. 64b upzp. Formularz jest OBLIGATORYJNY: wzór z rozporządzenia MRiT z 20.02.2024 r.
 * (Dz.U. 2024 poz. 351) — https://www.dziennikustaw.gov.pl/DU/2024/351/D2024000035101.pdf, pobrany i odczytany
 * 22.10.2026. Odwzorowano pkt 2–10 i załączniki A–E wzoru (pkt 1 „organ” i 11 „podpis” zastępuje EBI); pkt 2 wzoru
 * (rodzaj wniosku) jest wariantem w BE. Działki — wyłącznie identyfikatorem (przypis 6: postać elektroniczna).
 * Załączniki B i C (budynki, obiekty) są listami — każda pozycja to jeden egzemplarz załącznika wzoru.
 * Opłata 598 zł i zwolnienie właściciela / użytkownika wieczystego — wg katalogu i karty usługi BIP Kraków (AU-1);
 * opłata od decyzji o lokalizacji inwestycji celu publicznego nie została ustalona — urząd informuje petenta.
 */
const WZ: EbiCondition = { field: "rodzajWniosku", equals: "wz" };
const PUBLIC_PURPOSE: EbiCondition = { field: "rodzajWniosku", equals: "lokalizacjaIcp" };
const PART_OF_PARCEL: EbiCondition = { field: "sposobWyznaczenia", equals: "czesc" };
const FEE_WAIVED: EbiCondition = {
  any: [
    { field: "wlascicielTerenu", equals: "tak" },
    { field: "budownictwoMieszkaniowe", equals: true },
  ],
};
const ROAD_ACCESS: EbiCondition = { field: "dostepDrogi", in: ["bezposredni", "posredniDroga", "posredniSluzebnosc"] };
const INDIRECT_ACCESS: EbiCondition = { field: "dostepDrogi", in: ["posredniDroga", "posredniSluzebnosc"] };
const BUILDINGS: EbiCondition = { field: "zalacznikiFormularza", includes: "budynki" };
const STRUCTURES: EbiCondition = { field: "zalacznikiFormularza", includes: "obiekty" };
const LANDFILL: EbiCondition = { field: "zalacznikiFormularza", includes: "skladowisko" };
const MAJOR_ACCIDENT: EbiCondition = { field: "inwestycjaDotyczy", includes: "zakladZdr" };

/** Wiersz tabeli parametrów wzoru: wartość istniejąca oraz projektowana minimalna i maksymalna (puste = nie dotyczy). */
const parameterRow = (code: string, label: string, decimals?: number): IEbiField[] =>
  (
    [
      ["Istniejaca", "istniejąca"],
      ["Min", "projektowana minimalna"],
      ["Max", "projektowana maksymalna"],
    ] as const
  ).map(
    ([suffix, kind]): IEbiField => ({ code: `${code}${suffix}`, type: "number", label: `${label} — ${kind}`, decimals, min: 0, width: "third" }),
  );

/** Wiersz tabeli A.1 (infrastruktura techniczna): rozwiązania istniejące i projektowane + zapotrzebowanie. */
const infrastructure = (code: string, label: string, kinds: [string, string][]): IEbiField[] => {
  const hasOther = kinds.some(([value]) => value === "inne");

  return [
    {
      code,
      type: "checkboxes",
      label,
      hint: "Zaznacz rozwiązania istniejące i projektowane; nie zaznaczaj nic, jeśli nie dotyczy.",
      options: kinds.flatMap(([value, text]) => [
        { value: `${value}Istn`, label: `${text} — istniejące` },
        { value: `${value}Proj`, label: `${text} — projektowane` },
      ]),
    },
    {
      code: `${code}Info`,
      type: "text",
      label: `${label} — zapotrzebowanie oraz dodatkowe informacje`,
      maxLength: 500,
      placeholder: "np. 3 m³/miesiąc",
      hint: hasOther ? "Obowiązkowo, gdy zaznaczono „inne”. Zapotrzebowanie podaj z jednostką i czasem." : "Zapotrzebowanie podaj z jednostką i czasem.",
      requiredWhen: hasOther ? { any: [{ field: code, includes: "inneIstn" }, { field: code, includes: "inneProj" }] } : undefined,
    },
  ];
};

const WORKS = [
  { value: "budowa", label: "budowa" },
  { value: "odbudowa", label: "odbudowa" },
  { value: "rozbudowa", label: "rozbudowa" },
  { value: "nadbudowa", label: "nadbudowa" },
  { value: "przebudowa", label: "przebudowa" },
  { value: "zmianaSposobu", label: "zmiana sposobu użytkowania" },
  { value: "inne", label: "inne" },
];

const A1: IEbiFormDefinition = {
  code: "A1",
  version: 1,
  title: "Wniosek o ustalenie lokalizacji inwestycji celu publicznego albo warunków zabudowy",
  category: EEbiFormCategory.Planning,
  office: EEbiFormOffice.Municipality,
  description:
    "Decyzja o warunkach zabudowy (WZ) albo o lokalizacji inwestycji celu publicznego dla terenu bez planu miejscowego — urzędowy formularz z rozporządzenia.",
  keywords: ["warunki zabudowy", "wz", "decyzja", "lokalizacja inwestycji celu publicznego", "ulicp", "budowa", "zabudowa"],
  organ: "Wójt / Burmistrz / Prezydent Miasta",
  legalBasis:
    "art. 52 ust. 1, art. 59, art. 61, art. 64 ust. 1 i art. 64b ustawy z dnia 27 marca 2003 r. o planowaniu i zagospodarowaniu przestrzennym; rozporządzenie Ministra Rozwoju i Technologii z dnia 20 lutego 2024 r. w sprawie określenia wzoru formularza wniosku o ustalenie lokalizacji inwestycji celu publicznego albo warunków zabudowy",
  request:
    "Na podstawie art. 52 ust. 1 oraz art. 64 ust. 1 ustawy z dnia 27 marca 2003 r. o planowaniu i zagospodarowaniu przestrzennym wnoszę o wydanie decyzji wskazanej w pkt 2 dla inwestycji opisanej we wniosku.",
  feeLabel: "do 598 zł",
  fee: "Decyzja o warunkach zabudowy — 598 zł (część I pkt 8 załącznika do ustawy o opłacie skarbowej); zwolnieni: właściciel lub użytkownik wieczysty terenu oraz sprawy budownictwa mieszkaniowego. Pełnomocnictwo — 17 zł.",
  deadlineLabel: "65–90 dni",
  deadline:
    "Lokalizacja inwestycji celu publicznego — 65 dni (art. 51 ust. 2 upzp); warunki zabudowy — 90 dni, krócej dla niektórych inwestycji (art. 64 ust. 1 upzp).",
  variantField: "rodzajWniosku",
  variants: {
    wz: { title: "Wniosek o ustalenie warunków zabudowy" },
    lokalizacjaIcp: { title: "Wniosek o ustalenie lokalizacji inwestycji celu publicznego" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "2. Rodzaj wniosku",
      fields: [
        {
          code: "rodzajWniosku",
          type: "radio",
          label: "Wniosek",
          required: true,
          defaultValue: "wz",
          options: [
            { value: "wz", label: "o ustalenie warunków zabudowy" },
            {
              value: "lokalizacjaIcp",
              label: "o ustalenie lokalizacji inwestycji celu publicznego (cele publiczne w rozumieniu art. 6 ustawy o gospodarce nieruchomościami)",
            },
          ],
        },
      ],
    },
    applicantSection("3. Dane wnioskodawcy"),
    {
      code: "tytulDoTerenu",
      title: "3.1. Tytuł do terenu",
      fields: [
        {
          code: "wlascicielTerenu",
          type: "radio",
          label: "Czy wnioskodawca jest właścicielem lub użytkownikiem wieczystym terenu objętego wnioskiem?",
          required: true,
          options: YES_NO,
          hint: "Gdy działa pełnomocnik — odpowiedz za mocodawcę.",
        },
      ],
    },
    { ...representationSection(), title: "5. Pełnomocnik — w czyim imieniu składasz wniosek" },
    {
      code: "teren",
      title: "6. Teren objęty wnioskiem",
      fields: [
        { code: "terenUlica", type: "text", label: "Ulica i numer domu (nieobowiązkowo)", maxLength: 200 },
        { code: "terenKodPocztowy", type: "postalCode", label: "Kod pocztowy (nieobowiązkowo)", width: "third", placeholder: "00-000" },
        { code: "terenMiejscowosc", type: "text", label: "Miejscowość (nieobowiązkowo)", width: "twoThirds", maxLength: 100 },
        parcelIdsField("dzialki", "Działki ewidencyjne objęte wnioskiem"),
      ],
    },
    {
      code: "charakterystyka",
      title: "7. Charakterystyka inwestycji",
      description: "W tabelach parametrów zostaw puste pola, które nie dotyczą inwestycji.",
      fields: [
        { code: "nazwaInwestycji", type: "text", label: "7.1. Nazwa inwestycji", required: true, maxLength: 300 },
        {
          code: "sposobWyznaczenia",
          type: "radio",
          label: "7.2. Sposób wyznaczenia terenu inwestycji wskazanego w pkt 6",
          required: true,
          options: [
            { value: "cala", label: "Teren inwestycji obejmuje całą działkę ewidencyjną lub działki ewidencyjne" },
            { value: "czesc", label: "Teren inwestycji obejmuje część działki ewidencyjnej lub działek ewidencyjnych" },
          ],
        },
        {
          code: "powierzchniaTerenu",
          type: "number",
          label: "7.2.1. Powierzchnia terenu inwestycji [m²]",
          required: true,
          visibleWhen: PART_OF_PARCEL,
          decimals: 2,
          min: 0,
          width: "half",
          hint: "Granice terenu pokaż w formie graficznej na kopii mapy (załącznik).",
        },
        {
          code: "istniejacaZabudowa",
          type: "textarea",
          label: "7.3. Istniejąca zabudowa (gdy inwestycja dotyczy istniejącej zabudowy i zagospodarowania terenu)",
          maxLength: 2000,
        },
        { code: "obecneZagospodarowanie", type: "textarea", label: "7.4. Obecny sposób zagospodarowania terenu inwestycji", required: true, maxLength: 2000 },
        {
          code: "projektowaneZagospodarowanie",
          type: "textarea",
          label: "7.5. Projektowany sposób zagospodarowania terenu inwestycji",
          required: true,
          maxLength: 2000,
        },
        ...parameterRow("powZabudowy", "7.6.1. Powierzchnia zabudowy [m²]", 2),
        ...parameterRow("powBiologicznieCzynna", "7.6.2. Powierzchnia biologicznie czynna [m²]", 2),
        ...parameterRow("powPrzeksztalcana", "7.6.3. Powierzchnia podlegająca przekształceniu [m²]", 2),
        {
          code: "oddzialywanie",
          type: "textarea",
          label: "7.7. Sposób oddziaływania, w przypadku gdy obszar oddziaływania obiektu wykracza poza teren inwestycji",
          maxLength: 2000,
        },
        {
          code: "wplywNaSrodowisko",
          type: "textarea",
          label:
            "7.8. Dane charakteryzujące wpływ inwestycji na środowisko, w szczególności rodzaj przedsięwzięcia w nawiązaniu do przepisów odrębnych — gdy inwestycja zalicza się do przedsięwzięć mogących znacząco oddziaływać na środowisko",
          maxLength: 2000,
        },
        {
          code: "inwestycjaDotyczy",
          type: "checkboxes",
          label: "7.9. Czy inwestycja dotyczy",
          options: [
            {
              value: "dom70",
              label:
                "wolnostojącego, nie więcej niż dwukondygnacyjnego budynku mieszkalnego jednorodzinnego o powierzchni zabudowy do 70 m², o którym mowa w art. 29 ust. 1 pkt 1a ustawy — Prawo budowlane",
            },
            {
              value: "biogazownia",
              label:
                "biogazowni rolniczej spełniającej warunki określone w art. 4 ust. 1 ustawy z dnia 13 lipca 2023 r. o ułatwieniach w przygotowaniu i realizacji inwestycji w zakresie biogazowni rolniczych, a także ich funkcjonowaniu",
            },
            {
              value: "siecDystrybucyjna",
              label:
                "strategicznej inwestycji w zakresie sieci dystrybucyjnej, o której mowa w art. 1 ust. 2 pkt 4a ustawy z dnia 24 lipca 2015 r. o przygotowaniu i realizacji strategicznych inwestycji w zakresie sieci przesyłowych",
            },
            {
              value: "zakladZdr",
              label:
                "zakładu o zwiększonym lub dużym ryzyku wystąpienia poważnej awarii przemysłowej, o którym mowa w art. 248 ust. 1 ustawy — Prawo ochrony środowiska (wypełnij załącznik E)",
            },
          ],
        },
        {
          code: "powierzchniaSprzedazy",
          type: "number",
          label: "7.10. Łączna powierzchnia sprzedaży [m²] — gdy inwestycja dotyczy obiektu handlowego",
          decimals: 2,
          min: 0,
          width: "half",
        },
        {
          code: "powierzchniaGospodarstwa",
          type: "number",
          label: "7.11. Powierzchnia gospodarstwa rolnego [m²] — gdy inwestycja jest związana z gospodarstwem rolnym",
          decimals: 2,
          min: 0,
          width: "half",
        },
        { code: "dodatkoweInformacje", type: "textarea", label: "7.12. Dodatkowe informacje dotyczące terenu inwestycji (nieobowiązkowo)", maxLength: 2000 },
        {
          code: "zalacznikiFormularza",
          type: "checkboxes",
          label: "Inwestycja obejmuje (wypełnisz odpowiednie załączniki formularza)",
          options: [
            { value: "budynki", label: "budynki — załącznik B" },
            { value: "obiekty", label: "obiekty budowlane niebędące budynkami — załącznik C" },
            { value: "skladowisko", label: "składowisko odpadów — załącznik D" },
          ],
        },
      ],
    },
    {
      code: "komunikacja",
      title: "8. Obsługa komunikacyjna",
      fields: [
        {
          code: "dostepDrogi",
          type: "radio",
          label: "8.1. Dostęp do drogi publicznej",
          required: true,
          options: [
            { value: "nieDotyczy", label: "nie dotyczy" },
            { value: "bezposredni", label: "bezpośredni" },
            { value: "posredniDroga", label: "pośredni (droga wewnętrzna)" },
            { value: "posredniSluzebnosc", label: "pośredni (służebność)" },
          ],
        },
        {
          ...parcelIdsField(
            "dzialkiDostepu",
            "Działki ewidencyjne zapewniające dostęp do drogi publicznej",
            "Przy dostępie pośrednim wskaż wszystkie działki umożliwiające dostęp wraz z działką obejmującą drogę publiczną.",
          ),
          visibleWhen: ROAD_ACCESS,
        },
        { code: "dostepInfo", type: "textarea", label: "8.1.1. Dodatkowe informacje dotyczące dostępu do drogi publicznej (nieobowiązkowo)", maxLength: 1000 },
        ...parameterRow("parkingGaraz", "8.2.1. Miejsca do parkowania — garaż"),
        ...parameterRow("parkingParking", "8.2.2. Miejsca do parkowania — parking"),
        ...parameterRow("parkingInne", "8.2.3. Miejsca do parkowania — inne"),
        { code: "parkingInfo", type: "textarea", label: "8.2.4. Dodatkowe informacje dotyczące miejsc do parkowania (nieobowiązkowo)", maxLength: 1000 },
      ],
    },
    {
      code: "infrastruktura",
      title: "A. Załącznik — dane dotyczące infrastruktury technicznej",
      fields: [
        ...infrastructure("woda", "A.1.1. Dostęp do wody", [
          ["przylacze", "przyłącze wodociągowe"],
          ["studnia", "studnia do poboru wody pitnej"],
          ["inne", "inne"],
        ]),
        ...infrastructure("scieki", "A.1.2. Odprowadzanie ścieków", [
          ["przylacze", "przyłącze kanalizacyjne"],
          ["zbiornik", "bezodpływowy zbiornik na nieczystości ciekłe"],
          ["oczyszczalnia", "indywidualna oczyszczalnia ścieków"],
          ["inne", "inne"],
        ]),
        ...infrastructure("sciekiInne", "A.1.3. Odprowadzanie i oczyszczanie ścieków innych niż w pkt A.1.2", [
          ["przylacze", "przyłącze kanalizacyjne po wcześniejszym podczyszczeniu"],
          ["zbiornik", "bezodpływowy zbiornik na ścieki"],
          ["oczyszczalnia", "indywidualna oczyszczalnia ścieków"],
          ["zbiornikOdzwierzece", "bezodpływowy zbiornik na ścieki odzwierzęce"],
          ["plyta", "płyta obornikowa"],
          ["inne", "inne"],
        ]),
        ...infrastructure("wodyOpadowe", "A.1.4. Odprowadzenie wód opadowych i roztopowych", [
          ["przylacze", "przyłącze kanalizacji deszczowej"],
          ["teren", "na własny nieutwardzony teren inwestycji"],
          ["doly", "na własny teren inwestycji, do dołów chłonnych lub zbiorników retencyjnych"],
          ["inne", "inne"],
        ]),
        ...infrastructure("energia", "A.1.5. Dostęp do energii elektrycznej", [
          ["przylacze", "przyłącze do sieci"],
          ["oze", "instalacja pozyskująca energię elektryczną ze źródeł odnawialnych"],
          ["inne", "inne"],
        ]),
        ...infrastructure("gaz", "A.1.6. Dostęp do gazu", [
          ["przylacze", "przyłącze do sieci"],
          ["zbiornikNaziemny", "naziemny zbiornik na gaz płynny"],
          ["zbiornikPodziemny", "podziemny zbiornik na gaz płynny"],
          ["inne", "inne"],
        ]),
        ...infrastructure("cieplo", "A.1.7. Źródło ciepła", [
          ["przylacze", "przyłącze do sieci ciepłowniczej"],
          ["indywidualne", "indywidualne źródło ciepła"],
          ["oze", "instalacja pozyskująca energię cieplną ze źródeł odnawialnych"],
          ["inne", "inne"],
        ]),
        ...infrastructure("odpady", "A.1.8. Gospodarka odpadami", [
          ["regulamin", "zgodnie z regulaminem utrzymania czystości i porządku na terenie gminy"],
          ["inne", "inne"],
        ]),
        ...infrastructure("lacznosc", "A.1.9. Zaopatrzenie w środki łączności", [["przylacze", "przyłącze do sieci"]]),
        { code: "infrastrukturaInfo", type: "textarea", label: "A.2. Dodatkowe informacje z zakresu infrastruktury technicznej (nieobowiązkowo)", maxLength: 2000 },
      ],
    },
    {
      code: "budynki",
      title: "B. Załącznik — dane dotyczące budynku",
      visibleWhen: BUILDINGS,
      description: "Dodaj osobną pozycję dla każdego budynku o innych parametrach; budynki o takich samych parametrach opisz raz (pkt B.9).",
      fields: [
        {
          code: "budynki",
          type: "repeat",
          label: "Budynki",
          itemLabel: "Budynek (załącznik B)",
          required: true,
          min: 1,
          max: 20,
          fields: [
            { code: "nazwa", type: "text", label: "B.1. Nazwa budynku (nieobowiązkowo)", maxLength: 200 },
            {
              code: "funkcja",
              type: "text",
              label: "B.2. Funkcja budynku (zgodnie z Polską Klasyfikacją Obiektów Budowlanych)",
              required: true,
              maxLength: 200,
            },
            { code: "lokaleIstniejace", type: "number", label: "B.2.1. Liczba lokali mieszkalnych — istniejąca", min: 0, width: "half" },
            { code: "lokaleProjektowane", type: "number", label: "B.2.1. Liczba lokali mieszkalnych — projektowana", min: 0, width: "half" },
            { code: "rodzajPrac", type: "select", label: "B.3. Rodzaj prac dotyczących budynku", required: true, width: "half", options: WORKS },
            { code: "rodzajPracInne", type: "text", label: "Inne lub dodatkowe rodzaje prac", width: "half", maxLength: 200 },
            ...parameterRow("szerokoscElewacji", "B.4.1. Szerokość elewacji frontowej [m]", 2),
            ...parameterRow("powNadziemnych", "B.4.2. Suma powierzchni kondygnacji nadziemnych [m²]", 2),
            ...parameterRow("kondygnacjeNadziemne", "B.4.3. Liczba kondygnacji nadziemnych"),
            ...parameterRow("powPodziemnych", "B.4.4. Suma powierzchni kondygnacji podziemnych [m²]", 2),
            ...parameterRow("kondygnacjePodziemne", "B.4.5. Liczba kondygnacji podziemnych"),
            ...parameterRow("wysokosc", "B.4.6. Wysokość [m]", 2),
            ...parameterRow("katDachu", "B.4.7. Kąt nachylenia dachu [stopnie]"),
            {
              code: "poddasze",
              type: "select",
              label: "B.4.8. Rodzaj poddasza",
              width: "half",
              options: [
                { value: "uzytkowe", label: "użytkowe" },
                { value: "nieuzytkowe", label: "nieużytkowe" },
                { value: "nieDotyczy", label: "nie dotyczy" },
              ],
            },
            {
              code: "dach",
              type: "select",
              label: "B.5.1. Rodzaj projektowanego dachu",
              width: "half",
              options: [
                { value: "plaski", label: "płaski" },
                { value: "jednospadowy", label: "jednospadowy" },
                { value: "dwuspadowy", label: "dwuspadowy" },
                { value: "czterospadowy", label: "czterospadowy" },
                { value: "wielospadowy", label: "wielospadowy" },
                { value: "bezZmian", label: "bez zmian" },
                { value: "inny", label: "inny" },
              ],
            },
            {
              code: "polacie",
              type: "select",
              label: "B.5.2. Projektowany układ głównych połaci dachu",
              width: "half",
              options: [
                { value: "symetryczny", label: "symetryczny" },
                { value: "bezZmian", label: "bez zmian" },
                { value: "inny", label: "inny" },
              ],
            },
            {
              code: "kalenica",
              type: "select",
              label: "B.5.3. Projektowany kierunek głównej kalenicy w stosunku do frontu działki",
              width: "half",
              options: [
                { value: "prostopadly", label: "prostopadły" },
                { value: "rownolegly", label: "równoległy" },
                { value: "bezZmian", label: "bez zmian" },
                { value: "inny", label: "inny" },
              ],
            },
            { code: "dachInfo", type: "text", label: "B.5. Dodatkowe informacje o dachu (nieobowiązkowo)", maxLength: 500 },
            {
              code: "pokrycie",
              type: "text",
              label: "Projektowany rodzaj i kolor pokrycia dachowego — w przypadku obiektów lub terenu objętych ochroną konserwatorską",
              maxLength: 300,
            },
            {
              code: "odlegloscZOknami",
              type: "select",
              label: "B.6. Odległość budynku zwróconego ścianą z oknami lub drzwiami od granicy działki budowlanej",
              width: "half",
              options: [
                { value: "min4", label: "4 m i więcej" },
                { value: "ponizej4", label: "mniej niż 4 m" },
              ],
            },
            {
              code: "odlegloscBezOkien",
              type: "select",
              label: "B.7. Odległość budynku zwróconego ścianą bez okien i drzwi od granicy działki budowlanej",
              width: "half",
              options: [
                { value: "min3", label: "3 m i więcej" },
                { value: "ponizej3", label: "mniej niż 3 m" },
              ],
            },
            {
              code: "inneParametry",
              type: "textarea",
              label:
                "B.8. Inne parametry budynku, w tym wynikające z ochrony konserwatorskiej oraz z położenia na obszarach szczególnego zagrożenia powodzią",
              maxLength: 2000,
            },
            { code: "liczbaTakich", type: "number", label: "B.9. Liczba budynków o takich samych parametrach", min: 1, width: "half" },
          ],
        },
      ],
    },
    {
      code: "obiekty",
      title: "C. Załącznik — dane dotyczące obiektu budowlanego niebędącego budynkiem",
      visibleWhen: STRUCTURES,
      fields: [
        {
          code: "obiekty",
          type: "repeat",
          label: "Obiekty budowlane niebędące budynkami",
          itemLabel: "Obiekt (załącznik C)",
          required: true,
          min: 1,
          max: 20,
          fields: [
            { code: "nazwa", type: "text", label: "C.1. Nazwa obiektu (nieobowiązkowo)", maxLength: 200 },
            {
              code: "rodzajObiektu",
              type: "select",
              label: "C.2. Rodzaj obiektu niebędącego budynkiem",
              required: true,
              width: "half",
              options: [
                { value: "budowla", label: "budowla" },
                { value: "urzadzenieWodne", label: "urządzenie wodne" },
                { value: "inne", label: "inne" },
              ],
            },
            { code: "rodzajObiektuInne", type: "text", label: "Rodzaj obiektu — inne", width: "half", maxLength: 200 },
            { code: "rodzajPrac", type: "select", label: "C.3. Rodzaj prac dotyczących obiektu", required: true, width: "half", options: WORKS },
            { code: "rodzajPracInne", type: "text", label: "Inne lub dodatkowe rodzaje prac", width: "half", maxLength: 200 },
            ...parameterRow("powierzchnia", "C.4.1. Powierzchnia [m²]", 2),
            ...parameterRow("kubatura", "C.4.2. Kubatura, objętość, pojemność [m³]", 2),
            ...parameterRow("dlugosc", "C.4.3. Długość [m]", 2),
            ...parameterRow("szerokosc", "C.4.4. Szerokość [m]", 2),
            ...parameterRow("wysokosc", "C.4.5. Wysokość [m]", 2),
            { code: "inneParametry", type: "textarea", label: "C.4.6. Inne parametry obiektu budowlanego (nieobowiązkowo)", maxLength: 2000 },
            { code: "liczbaTakich", type: "number", label: "C.5. Liczba obiektów o takich samych parametrach", min: 1, width: "half" },
          ],
        },
      ],
    },
    {
      code: "skladowisko",
      title: "D. Załącznik — dane dotyczące składowiska odpadów",
      visibleWhen: LANDFILL,
      fields: [
        { code: "skladowiskoRzedna", type: "text", label: "D.1. Docelowa rzędna składowiska odpadów", required: true, maxLength: 100 },
        {
          code: "skladowiskoRocznie",
          type: "textarea",
          label: "D.2. Roczna ilość składowanych odpadów oraz rodzaje składowanych odpadów (z jednostką miary)",
          required: true,
          maxLength: 2000,
        },
        {
          code: "skladowiskoCalkowicie",
          type: "textarea",
          label: "D.3. Całkowita ilość składowanych odpadów oraz rodzaje składowanych odpadów (z jednostką miary)",
          required: true,
          maxLength: 2000,
        },
        { code: "skladowiskoScieki", type: "textarea", label: "D.4. Sposób gromadzenia, oczyszczania i odprowadzania ścieków", required: true, maxLength: 2000 },
        {
          code: "skladowiskoGaz",
          type: "textarea",
          label: "D.5. Sposób gromadzenia, oczyszczania i wykorzystywania lub unieszkodliwiania gazu składowiskowego",
          required: true,
          maxLength: 2000,
        },
        { code: "skladowiskoInne", type: "textarea", label: "D.6. Inne parametry składowiska odpadów (nieobowiązkowo)", maxLength: 2000 },
      ],
    },
    {
      code: "zakladZdr",
      title: "E. Załącznik — dane dotyczące zakładu o zwiększonym lub dużym ryzyku wystąpienia poważnej awarii przemysłowej",
      visibleWhen: MAJOR_ACCIDENT,
      fields: [
        {
          code: "zakladKwalifikacja",
          type: "radio",
          label: "E.1. Kwalifikacja inwestycji",
          required: true,
          options: [
            { value: "zwiekszone", label: "zakład o zwiększonym ryzyku wystąpienia poważnej awarii przemysłowej" },
            { value: "duze", label: "zakład o dużym ryzyku wystąpienia poważnej awarii przemysłowej" },
          ],
        },
        {
          code: "zakladSubstancje",
          type: "textarea",
          label:
            "E.2. Przewidywane rodzaje i ilości substancji niebezpiecznych w zakładzie, decydujących o zaliczeniu do zakładu o zwiększonym lub dużym ryzyku",
          required: true,
          maxLength: 3000,
        },
        {
          code: "zakladZasieg",
          type: "radio",
          label: "E.3. Przewidywany zasięg potencjalnych skutków poważnych awarii przemysłowych wykracza poza teren inwestycji",
          required: true,
          options: YES_NO,
        },
        {
          code: "zakladSkutki",
          type: "textarea",
          label:
            "E.4. Informacje i dane na temat obszaru, skali i prawdopodobieństwa wystąpienia potencjalnych skutków poważnych awarii przemysłowych wykraczających poza teren inwestycji, z uwzględnieniem wartości parametrów granicznych w zakresie palności, wybuchowości i toksyczności substancji niebezpiecznych",
          required: true,
          visibleWhen: { field: "zakladZasieg", equals: "tak" },
          maxLength: 5000,
        },
        { code: "zakladInfo", type: "textarea", label: "E.5. Dodatkowe informacje", maxLength: 2000 },
      ],
    },
    {
      code: "zalaczniki",
      title: "10. Załączniki — pytania",
      description: "Odpowiedzi ustalają, które załączniki z pkt 10 wzoru trzeba dołączyć w kolejnym kroku.",
      fields: [
        {
          code: "postacMapy",
          type: "radio",
          label: "Mapa zasadnicza lub, w przypadku jej braku, mapa ewidencyjna — w postaci",
          required: true,
          options: [
            { value: "elektroniczna", label: "elektronicznej, w obowiązującym państwowym układzie odniesień przestrzennych" },
            { value: "papierowa", label: "papierowej (dołączam skan)" },
          ],
        },
        {
          code: "decyzjaSrodowiskowa",
          type: "radio",
          label:
            "Czy dla inwestycji wymagana jest decyzja o środowiskowych uwarunkowaniach (art. 71 ust. 1 ustawy o udostępnianiu informacji o środowisku i jego ochronie, udziale społeczeństwa w ochronie środowiska oraz o ocenach oddziaływania na środowisko)?",
          required: true,
          options: YES_NO,
        },
        {
          code: "uzbrojenieWystarczajace",
          type: "radio",
          label: "Czy istniejące uzbrojenie terenu jest wystarczające dla zamierzenia inwestycyjnego?",
          required: true,
          options: YES_NO,
        },
        {
          code: "obszarPowodzi",
          type: "radio",
          label: "Czy inwestycja znajduje się na obszarze szczególnego zagrożenia powodzią?",
          required: true,
          options: YES_NO,
        },
      ],
    },
    {
      code: "oplata",
      title: "Opłata skarbowa",
      fields: [
        {
          code: "budownictwoMieszkaniowe",
          type: "checkbox",
          label: "Wniosek dotyczy budownictwa mieszkaniowego — zwolnienie z opłaty skarbowej (art. 2 ust. 1 pkt 2 ustawy o opłacie skarbowej)",
          visibleWhen: WZ,
        },
        {
          code: "kwotaOplaty",
          type: "fee",
          label: "Opłata skarbowa od decyzji o warunkach zabudowy",
          feeAmount: 598,
          feeWaivedWhen: FEE_WAIVED,
          visibleWhen: WZ,
          hint: "Zwolnieni: właściciel lub użytkownik wieczysty terenu objętego wnioskiem (pkt 3.1) oraz sprawy budownictwa mieszkaniowego.",
        },
        {
          code: "infoOplataIcp",
          type: "info",
          label: "Urząd poinformuje, czy od decyzji o ustaleniu lokalizacji inwestycji celu publicznego należy się opłata skarbowa i w jakiej wysokości.",
          visibleWhen: PUBLIC_PURPOSE,
        },
      ],
    },
    electronicDeliverySection("9. Oświadczenie w sprawie korespondencji elektronicznej i sposób odbioru"),
  ],
  attachments: [
    {
      code: "mapa",
      label: "Mapa zasadnicza lub, w przypadku jej braku, mapa ewidencyjna",
      hint: "Postać elektroniczna w państwowym układzie odniesień przestrzennych (np. z PODGiK) albo skan mapy papierowej.",
      required: true,
    },
    {
      code: "zagospodarowanie",
      label:
        "Określenie planowanego sposobu zagospodarowania terenu wraz z oznaczeniem frontu terenu oraz charakterystyki zabudowy i zagospodarowania terenu (przeznaczenie i gabaryty projektowanych obiektów, powierzchnia terenu podlegająca przekształceniu) — w formie graficznej",
      required: true,
    },
    {
      code: "graniceTerenu",
      label: "Określenie granic terenu objętego wnioskiem, pokazanych na kopii mapy, wraz z podaniem wymiarów",
      hint: "Gdy teren inwestycji stanowi część działki ewidencyjnej lub działek ewidencyjnych.",
      visibleWhen: PART_OF_PARCEL,
      required: true,
    },
    ...proxyAttachments(),
    feeProofAttachment("Potwierdzenie uiszczenia opłaty skarbowej od decyzji o warunkach zabudowy (598 zł)", {
      visibleWhen: { all: [WZ, { not: FEE_WAIVED }] },
    }),
    {
      code: "decyzjaDsu",
      label: "Kopia decyzji o środowiskowych uwarunkowaniach (art. 71 ust. 1 ustawy ooś)",
      visibleWhen: { field: "decyzjaSrodowiskowa", equals: "tak" },
      required: true,
    },
    {
      code: "infrastrukturaDokumenty",
      label:
        "Dokumenty potwierdzające zapewnienie dostępu inwestycji do infrastruktury technicznej, w tym dostaw energii, dostaw wody oraz odbioru ścieków bytowych",
      hint: "Np. warunki lub umowy przyłączenia — gdy istniejące uzbrojenie terenu nie jest wystarczające.",
      visibleWhen: { field: "uzbrojenieWystarczajace", equals: "nie" },
      required: true,
      multiple: true,
    },
    {
      code: "awarie",
      label:
        "Obszar wystąpienia potencjalnych skutków poważnych awarii przemysłowych w czytelnej technice graficznej (gdy wychodzą poza teren inwestycji) albo odrębne opracowanie analityczno-ocenne",
      visibleWhen: MAJOR_ACCIDENT,
      required: true,
    },
    {
      code: "prawoWodne",
      label: "Dokumenty wynikające z ustawy — Prawo wodne (inwestycja na obszarze szczególnego zagrożenia powodzią)",
      visibleWhen: { field: "obszarPowodzi", equals: "tak" },
      required: true,
      multiple: true,
    },
    {
      code: "dostepDoDrogi",
      label: "Dokumenty, które pozwolą ocenić dostęp do drogi publicznej (np. służebność, umowa)",
      hint: "Nieobowiązkowo — przy dostępie pośrednim.",
      visibleWhen: INDIRECT_ACCESS,
      multiple: true,
    },
    { ...otherAttachments(), hint: "Inne dokumenty, np. licencja mapy (nieobowiązkowo)." },
  ],
};

export default A1;
